import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import axios from 'axios';
import handleOpenSweetAlert from '../../../components/handleOpenSweetAlert';
import { useSession } from '../../../components/hooks/useSession';
import { getTablaNivelAut } from '../../../services/nivelAutService';
import config from '../../../config/config';

// ─── Helpers ───────────────────────────────────────────────────────────────────

const getSessionString = (key) => sessionStorage.getItem(key) || '';
const getSessionArray = (key) => {
  try {
    const value = sessionStorage.getItem(key);
    if (!value) return [];
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const normalizeEmpresaArray = (idEmpresa) => {
  if (idEmpresa == null) return [];
  if (Array.isArray(idEmpresa)) {
    return idEmpresa.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
  }
  if (typeof idEmpresa === 'string') {
    const s = idEmpresa.trim();
    if (s.startsWith('[') && s.endsWith(']')) {
      try {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) return arr.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
      } catch {}
    }
    if (s.includes(',')) {
      return s.split(',').map((x) => Number(x.trim())).filter((n) => Number.isFinite(n) && n > 0);
    }
    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  }
  const n = Number(idEmpresa);
  return Number.isFinite(n) && n > 0 ? [n] : [];
};

// ─── Hook principal ────────────────────────────────────────────────────────────

export const useNivelAutController = () => {
  const apiUrl = config.apiBaseUrl;
  const { idPerfil } = useSession();

  // ─── Datos de sesion reactivos
  const [loggedInUsername, setLoggedInUsername] = useState(getSessionString('loggedInUsername'));
  const [idEmpresa, setIdEmpresa] = useState(getSessionArray('EmpresaId'));
  const [nombreEmpresa, setNombreEmpresa] = useState(getSessionString('EmpresaNombre'));

  const usuarioId = useMemo(() => Number(sessionStorage.getItem('UsuarioId') || 0), []);

  const empresaIdsKey = useMemo(() => {
    const arr = normalizeEmpresaArray(idEmpresa);
    return arr.length ? arr.join(',') : '';
  }, [idEmpresa]);

  const empresaIds = useMemo(() => {
    if (!empresaIdsKey) return [];
    return empresaIdsKey.split(',').map((n) => Number(n)).filter((x) => Number.isFinite(x) && x > 0);
  }, [empresaIdsKey]);

  const empresaIdPref = useMemo(() => {
    return empresaIds.length > 1 ? 0 : (empresaIds[0] ?? 0);
  }, [empresaIds]);

  // ─── Escucha cambio de empresa
  useEffect(() => {
    const handleEmpresaChange = () => {
      setLoggedInUsername(getSessionString('loggedInUsername'));
      setIdEmpresa(getSessionArray('EmpresaId'));
      setNombreEmpresa(getSessionString('EmpresaNombre'));
    };
    window.addEventListener('empresaCambiada', handleEmpresaChange);
    return () => window.removeEventListener('empresaCambiada', handleEmpresaChange);
  }, []);

  // ─── Estados de datos
  const [data, setData] = useState({ contenido: [], total: 0 });
  const [loadingData, setLoadingData] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [filteredData, setFilteredData] = useState(null); // <-- Nuevo estado para filtros locales

  // ─── Paginacion
  const [page, setPage] = useState(1);
  const pageRef = useRef(1);
  useEffect(() => { pageRef.current = page; }, [page]);
  const pageSize = 5000;

  // ─── Estado de tabla (global query + sort)
  const [tableState, setTableState] = useState({ globalQuery: '', sortField: null, sortDir: null });
  const tableStateRef = useRef(tableState);
  useEffect(() => { tableStateRef.current = tableState; }, [tableState]);

  // ─── Estados auxiliares (datos para modales)
  const [dataEmpresa, setDataEmpresa] = useState([]);
  const [dataDivisas, setDataDivisas] = useState([]);

  // ─── Modales y alertas
  const [modalOpen, setModalOpen] = useState(false);
  const [modalCentroCostoOpen, setModalCentroCostoOpen] = useState(false);
  const [modalUsuarioOpen, setModalUsuarioOpen] = useState(false);
  const [modalDepartamentoOpen, setModalDepartamentoOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, message: '', action: '', row: null });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');

  // ─── Formulario NivelAut
  const [formData, setFormData] = useState({ ID: 0, SESSION: loggedInUsername, Divisa: '' });
  const [formCentroCosto, setFormCentroCosto] = useState({ CENTRO_COSTO: '', NUMERO_CC: '', ID_CC: null, IDEMPRESA: empresaIdPref });
  const [formDepartamento, setFormDepartamento] = useState({ NOMBRE_DEPARTAMENTO: '', DEPARTAMENTOID: '', IDEMPRESA: empresaIdPref });
  const [nuevoUsuario, setNuevoUsuario] = useState({ USUARIOID: null, USERNAME: '', PASSWORD: '', CORREO: '', empresas: empresaIds, TIPOUSER: null, SESSION: loggedInUsername });
  const [dataRolesUsuario, setDataRolesUsuario] = useState([]);
  const [campoOrigenUsuario, setCampoOrigenUsuario] = useState(null);

  // ─── Datos dependientes para modales
  const [dataSucursal, setDataSucursal] = useState([]);
  const [dataCentrocosto, setDataCentrocosto] = useState([]);
  const [dataDepartamento, setDataDepartamento] = useState([]);
  const [dataUsuario, setDataUsuario] = useState([]);
  const [dataUsuarioAutorizadores, setDataUsuarioAutorizadores] = useState([]);
  const [dataUsuarioTesoreros, setDataUsuarioTesoreros] = useState([]);

  // ─── Excel
  const [excelData, setExcelData] = useState([]);
  const [filterData, setFilterData] = useState({});

  // ─── AbortController
  const abortRef = useRef(null);

  // ─── FETCH DATA (con soporte de paginacion acumulativa)
  const fetchData = useCallback(async (isLoadMore = false) => {
    if (!apiUrl || !empresaIdsKey) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const currentPage = isLoadMore === true ? (pageRef.current + 1) : 1;
    setLoadingData(true);

    try {
      const payload = {
        filterData: {
          globalQuery: tableStateRef.current.globalQuery || '',
          sortField: tableStateRef.current.sortField || null,
          sortDir: tableStateRef.current.sortDir || null,
          page: currentPage,
          pageSize,
        },
        nombreEmpresa,
        idEmpresa: empresaIds,
      };

      const respData = await getTablaNivelAut(payload, controller.signal);

      setData((prev) => {
        if (isLoadMore === true && respData?.contenido) {
          return {
            ...respData,
            contenido: [...(prev?.contenido || []), ...respData.contenido],
          };
        }
        return respData || { contenido: [], total: 0 };
      });

      setPage(currentPage);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Error fetching NivelAut data:', err);
      handleOpenSweetAlert('Error al cargar los niveles de autorizacion.', 'error');
    } finally {
      if (abortRef.current === controller) {
        setLoadingData(false);
        abortRef.current = null;
      }
    }
  }, [apiUrl, empresaIdsKey, empresaIds, nombreEmpresa, pageSize]);

  // ─── EXPORT FETCH ALL
  const handleExportFetchAll = useCallback(async () => {
    if (!apiUrl || !empresaIdsKey) return data;
    try {
      const payload = {
        filterData: {
          globalQuery: tableStateRef.current.globalQuery || '',
          sortField: tableStateRef.current.sortField || null,
          sortDir: tableStateRef.current.sortDir || null,
          page: 1,
          pageSize: Math.max(data?.total || 5000, 5000),
        },
        nombreEmpresa,
        idEmpresa: empresaIds,
      };
      const respData = await getTablaNivelAut(payload);
      return respData || { contenido: [], total: 0 };
    } catch (err) {
      console.error('Error fetching all NivelAut data for export:', err);
      return data;
    }
  }, [apiUrl, empresaIdsKey, empresaIds, nombreEmpresa, data]);

  // Carga inicial y cuando cambia tableState con debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData(false);
    }, 80);
    return () => {
      clearTimeout(timer);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchData, tableState]);

  // ─── Datos iniciales (empresas + divisas)
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        if (!empresaIdsKey) return;
        const [empresaRes, divisaRes] = await Promise.all([
          axios.post(`${apiUrl}/nivelAutEmpresa`, { idEmpresa: empresaIds, idPerfil }),
          axios.post(`${apiUrl}/nivelAutDivisas`, {}),
        ]);
        setDataEmpresa(empresaRes.data[0]);
        setDataDivisas(divisaRes.data);
      } catch (err) {
        console.error('Error al cargar datos iniciales:', err);
      }
    };
    fetchInitialData();
  }, [apiUrl, empresaIds, empresaIdsKey, idPerfil]);

  // ─── Ref para evitar doble petición de dependencias al abrir/copiar
  const lastLoadedEmpresaRef = useRef(null);

  // ─── Cargar dependencias cuando cambia Empresa en formData
  useEffect(() => {
    if (!formData.Empresa) {
      lastLoadedEmpresaRef.current = null;
      return;
    }
    const empresaId = Number(formData.Empresa);
    if (lastLoadedEmpresaRef.current === empresaId) {
      return; // Ya cargado por handleOpenModal, evitar duplicar llamadas a la API
    }
    lastLoadedEmpresaRef.current = empresaId;

    const cargarDependencias = async () => {
      const [sucRes, ccRes, depRes, autRes, tesRes, usuRes] = await Promise.all([
        axios.post(`${apiUrl}/nivelAutSucursal`, { EmpresaId: formData.Empresa }),
        axios.post(`${apiUrl}/nivelAutCentroCosto`, { EmpresaId: empresaId }),
        axios.post(`${apiUrl}/nivelAutDepartamento`, { EmpresaId: empresaId }),
        axios.post(`${apiUrl}/nivelAutUsuariosAutorizadores`, { EmpresaId: formData.Empresa }),
        axios.post(`${apiUrl}/nivelAutUsuariosTesoreros`, { EmpresaId: formData.Empresa }),
        axios.post(`${apiUrl}/nivelAutUsuarios`, { idEmpresa: empresaId }),
      ]);
      setDataSucursal(sucRes.data);
      setDataCentrocosto(ccRes.data);
      setDataDepartamento(depRes.data);
      setDataUsuarioAutorizadores(autRes.data);
      setDataUsuarioTesoreros(tesRes.data);
      setDataUsuario(usuRes.data[0]);
    };

    cargarDependencias().catch((err) => console.error(err));
  }, [apiUrl, formData.Empresa]);

  // ─── handleTableStateChange (callback para CollapsibleTable onStateChange)
  const handleTableStateChange = useCallback((newState) => {
    const newGQ = newState.globalQuery || '';
    const newSF = newState.sortConfig?.field || null;
    const newSD = newState.sortConfig?.direction || null;
    const cur = tableStateRef.current;
    if (cur.globalQuery !== newGQ || cur.sortField !== newSF || cur.sortDir !== newSD) {
      setTableState({ globalQuery: newGQ, sortField: newSF, sortDir: newSD });
      setPage(1);
    }
  }, []);

  // ─── Reset modales
  const resetModalStates = useCallback(() => {
    lastLoadedEmpresaRef.current = null;
    setFormData({ ID: 0, SESSION: loggedInUsername, Autorizadores: [], Tesoreros: [], Divisa: '' });
    setDataSucursal([]);
    setDataCentrocosto([]);
    setDataDepartamento([]);
    setDataUsuarioAutorizadores([]);
    setDataUsuarioTesoreros([]);
    setDataUsuario([]);
  }, [loggedInUsername]);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    resetModalStates();
  }, [resetModalStates]);

  // ─── Abrir modal editar/copiar
  const handleOpenModal = useCallback(async (row, isCopy = false) => {
    const empresaObj = dataEmpresa.find((emp) => emp.NOMBRE_EMPRESA === row.Empresa);
    const empresaId = empresaObj ? empresaObj.EMPRESAID : '';

    try {
      const [sucursalRes, centroCostoRes, departamentoRes, autorizadoresRes, tesorerosRes, divisaRes, usuarioRes] = await Promise.all([
        axios.post(`${apiUrl}/nivelAutSucursal`, { EmpresaId: empresaId }),
        axios.post(`${apiUrl}/nivelAutCentroCosto`, { EmpresaId: empresaId }),
        axios.post(`${apiUrl}/nivelAutDepartamento`, { EmpresaId: empresaId }),
        axios.post(`${apiUrl}/nivelAutUsuariosAutorizadores`, { EmpresaId: empresaId }),
        axios.post(`${apiUrl}/nivelAutUsuariosTesoreros`, { EmpresaId: empresaId }),
        axios.post(`${apiUrl}/nivelAutDivisas`, {}),
        axios.post(`${apiUrl}/nivelAutUsuarios`, { idEmpresa: empresaId }),
      ]);

      setDataSucursal(sucursalRes.data);
      setDataCentrocosto(centroCostoRes.data);
      setDataDepartamento(departamentoRes.data);
      setDataUsuarioAutorizadores(autorizadoresRes.data);
      setDataUsuarioTesoreros(tesorerosRes.data);
      setDataDivisas(divisaRes.data);
      setDataUsuario(usuarioRes.data[0]);
      lastLoadedEmpresaRef.current = Number(empresaId);

      const centroCostoObj = centroCostoRes.data.find((cc) => cc.CENTRO_COSTO === row.CentroCosto);

      const allUsersMap = new Map();
      [
        ...(autorizadoresRes.data || []),
        ...(tesorerosRes.data || []),
        ...((usuarioRes.data && usuarioRes.data[0]) || []),
      ].forEach((u) => {
        if (u && u.USER_NAME && u.USUARIOID) {
          const key = String(u.USER_NAME).trim().toLowerCase();
          if (!allUsersMap.has(key)) allUsersMap.set(key, u.USUARIOID);
        }
      });

      const getUserIdByName = (userName) => {
        if (!userName || userName === 'N/A') return null;
        const key = String(userName).trim().toLowerCase();
        return allUsersMap.get(key) || null;
      };

      const autorizadores = [];
      const tesoreros = [];

      for (let i = 1; i <= 10; i++) {
        const val = row[`AUTORIZADOR ${i}`] ?? row[`AUTORIZADOR${i}`];
        if (val && val !== 'N/A') {
          const uId = getUserIdByName(val);
          if (uId) autorizadores.push(uId);
        }
      }

      for (let i = 1; i <= 5; i++) {
        const val = row[`TESORERO ${i}`] ?? row[`TESORERO${i}`] ?? row[`TESORERO ${i}`];
        if (val && val !== 'N/A') {
          const uId = getUserIdByName(val);
          if (uId) tesoreros.push(uId);
        }
      }

      setFormData({
        ID: isCopy ? 0 : row.ID || '',
        Empresa: empresaId,
        Sucursal: sucursalRes.data.find((suc) => suc.NOMBRE_SUCURSAL === row.Sucursal)?.NUMERO_SUCURSAL || '',
        CentroCosto: isCopy ? null : centroCostoObj ? centroCostoObj.CCID : '',
        Departamento: isCopy ? null : departamentoRes.data.find((dep) => dep.NOMBRE_DEPARTAMENTO === row.Departamento)?.DEPARTAMENTOID || '',
        Comprador: getUserIdByName(row.Usuario) || '',
        SESSION: loggedInUsername || '',
        Autorizadores: autorizadores,
        Tesoreros: tesoreros,
        Divisa: divisaRes.data.find((div) => div.NOMBRE_DIVISA === row.Divisa)?.ID_DIVISA || '',
      });

      setModalOpen(true);
    } catch (error) {
      console.error('Error al cargar datos dependientes:', error);
      handleOpenSweetAlert('Error al cargar datos relacionados. Intente de nuevo.', 'error');
    }
  }, [apiUrl, dataEmpresa, loggedInUsername]);

  // ─── Abrir modal nuevo usuario
  const abrirModalNuevoUsuario = useCallback(async () => {
    try {
      const response = await axios.post(`${apiUrl}/usuariosTipoUsuario`);
      setDataRolesUsuario(response.data);
      setModalUsuarioOpen(true);
    } catch (err) {
      console.error('Error al cargar roles de usuario:', err);
    }
  }, [apiUrl]);

  // ─── Handle input change en formulario
  const handleInputChange = useCallback((fieldName, value) => {
    const camposConNuevo = ['CentroCosto', 'Departamento', 'Comprador', 'Autorizadores', 'Tesoreros'];
    const intentoAgregarNuevo = value === 'nuevo' || (Array.isArray(value) && value.includes('nuevo'));

    if (camposConNuevo.includes(fieldName) && intentoAgregarNuevo && !formData.Empresa) {
      setSnackbarMessage('Selecciona primero una empresa antes de agregar un nuevo registro.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    if (fieldName === 'CentroCosto' && value === 'nuevo') {
      setFormCentroCosto({ CENTRO_COSTO: '', NUMERO_CC: '', ID_CC: null, IDEMPRESA: empresaIdPref });
      setModalCentroCostoOpen(true);
      return;
    }

    if (fieldName === 'Comprador' && value === 'nuevo') {
      setCampoOrigenUsuario('Comprador');
      abrirModalNuevoUsuario();
      return;
    }

    if (fieldName === 'Departamento' && value === 'nuevo') {
      setFormDepartamento({ NOMBRE_DEPARTAMENTO: '', DEPARTAMENTOID: '', IDEMPRESA: empresaIdPref });
      setModalDepartamentoOpen(true);
      return;
    }

    if (fieldName === 'Autorizadores' && Array.isArray(value) && value.includes('nuevo')) {
      setCampoOrigenUsuario('Autorizadores');
      abrirModalNuevoUsuario();
      return;
    }

    if (fieldName === 'Tesoreros' && Array.isArray(value) && value.includes('nuevo')) {
      setCampoOrigenUsuario('Tesoreros');
      abrirModalNuevoUsuario();
      return;
    }

    if (fieldName === 'Autorizadores' || fieldName === 'Tesoreros') {
      let newArray = Array.isArray(value) ? [...value] : [];
      newArray = newArray.filter((item) => item !== 'nuevo');
      if (fieldName === 'Tesoreros') newArray = newArray.slice(0, 1);
      setFormData((prev) => ({ ...prev, [fieldName]: newArray }));
      return;
    }

    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  }, [formData.Empresa, empresaIdPref, abrirModalNuevoUsuario]);

  // ─── Crear/editar registro
  const crearEditarRegistro = useCallback(async () => {
    const requiredFields = [
      { name: 'Empresa', value: formData.Empresa },
      { name: 'Sucursal', value: formData.Sucursal },
      { name: 'CentroCosto', value: formData.CentroCosto },
      { name: 'Departamento', value: formData.Departamento },
      { name: 'Comprador', value: formData.Comprador },
    ];

    const missingFields = requiredFields.filter((f) => !f.value || f.value === '');
    if (missingFields.length > 0) {
      setSnackbarMessage(`Los siguientes campos son obligatorios: ${missingFields.map((f) => f.name).join(', ')}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    let cleanedAutorizadores = (formData.Autorizadores || []).filter((item) => item && item !== '' && !isNaN(item));
    let cleanedTesoreros = (formData.Tesoreros || []).filter((item) => item && item !== '' && !isNaN(item));

    if (cleanedAutorizadores.length === 0) {
      setSnackbarMessage('Debe seleccionar al menos un Autorizador valido.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (cleanedTesoreros.length === 0) {
      setSnackbarMessage('Debe seleccionar un Tesorero valido.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (cleanedTesoreros.length > 1) {
      setSnackbarMessage('Solo se permite un Tesorero.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    const updatedFormData = { ...formData, Autorizadores: cleanedAutorizadores, Tesoreros: cleanedTesoreros };

    setLoadingAction(true);
    try {
      const checkResponse = await axios.post(`${apiUrl}/nivelAutVerificarDuplicado`, { formData: updatedFormData });

      if (checkResponse.data?.existe) {
        setLoadingAction(false);
        setSnackbarMessage('Ya existe un registro con estos datos. No se puede crear/editar.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      await axios.post(`${apiUrl}/nivelAutCrearEditar/`, { formData: updatedFormData });

      // 1. Cerrar el modal de edición y apagar spinner de procesamiento
      setModalOpen(false);
      resetModalStates();
      setLoadingAction(false);

      // 2. Mostrar la alerta de éxito y esperar a que el usuario presione "Aceptar"
      await handleOpenSweetAlert('Se ha creado/actualizado con exito el Nivel de Autorizacion', 'success');

      // 3. Una vez cerrada la alerta, recargar los datos limpiamente
      fetchData(false);
    } catch (err) {
      console.error('Error:', err);
      setLoadingAction(false);
      await handleOpenSweetAlert('Hubo un error al crear el Nivel de Autorizacion', 'error');
      setModalOpen(false);
      resetModalStates();
    }
  }, [apiUrl, formData, fetchData, resetModalStates]);

  // ─── Cambiar estado (activar/desactivar)
  const cambiarEstadoNivelAut = useCallback(async (row) => {
    const nuevoEstado = row.ACTIVO ? 0 : 1;
    setLoadingAction(true);
    try {
      await axios.post(`${apiUrl}/nivelAutEliminar`, {
        valorBool: nuevoEstado,
        ID: row.ID,
        SESSION: loggedInUsername,
      });
      await handleOpenSweetAlert(
        nuevoEstado ? 'Nivel de Autorizacion activado' : 'Nivel de Autorizacion desactivado',
        'success'
      );
      fetchData(false);
    } catch (err) {
      console.error('Error al cambiar estado:', err);
      await handleOpenSweetAlert('Error al cambiar el estado del registro', 'error');
    } finally {
      setLoadingAction(false);
    }
  }, [apiUrl, loggedInUsername, fetchData]);



  // ─── Guardar nuevo usuario
  const handleGuardarNuevoUsuario = useCallback(async () => {
    try {
      await axios.post(`${apiUrl}/usuariosCrearEditar`, { formData: nuevoUsuario });

      setModalUsuarioOpen(false);
      setNuevoUsuario({ USUARIOID: null, USERNAME: '', PASSWORD: '', CORREO: '', empresas: empresaIds, TIPOUSER: null, SESSION: loggedInUsername });

      const empresaId = Number(formData.Empresa);
      const [usuarioRes, autorizadoresRes, tesorerosRes] = await Promise.all([
        axios.post(`${apiUrl}/nivelAutUsuarios`, { idEmpresa: empresaId }),
        axios.post(`${apiUrl}/nivelAutUsuariosAutorizadores`, { EmpresaId: empresaId }),
        axios.post(`${apiUrl}/nivelAutUsuariosTesoreros`, { EmpresaId: empresaId }),
      ]);

      setDataUsuario(usuarioRes.data[0]);
      setDataUsuarioAutorizadores(autorizadoresRes.data);
      setDataUsuarioTesoreros(tesorerosRes.data);

      const nuevo = usuarioRes.data[0].find((u) => u.USER_NAME === nuevoUsuario.USERNAME);

      if (nuevo) {
        setFormData((prev) => {
          if (campoOrigenUsuario === 'Autorizadores') {
            const nuevaLista = [...(prev.Autorizadores || [])];
            const indexVacio = nuevaLista.findIndex((val) => !val);
            if (indexVacio !== -1) nuevaLista[indexVacio] = nuevo.USUARIOID;
            else nuevaLista.push(nuevo.USUARIOID);
            return { ...prev, Autorizadores: nuevaLista };
          }
          if (campoOrigenUsuario === 'Tesoreros') return { ...prev, Tesoreros: [nuevo.USUARIOID] };
          return { ...prev, Comprador: nuevo.USUARIOID };
        });
        setCampoOrigenUsuario(null);
      }

      setSnackbarMessage('Usuario creado correctamente');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error al guardar el nuevo usuario:', error);
      await handleOpenSweetAlert('Error al guardar el nuevo usuario', 'error');
    }
  }, [apiUrl, nuevoUsuario, empresaIds, loggedInUsername, formData.Empresa, campoOrigenUsuario]);

  // ─── Confirm modal helpers
  const confirmarBorrar = useCallback((row) => {
    setConfirmModal({ open: true, action: 'borrar', row, message: 'Esta seguro de que desea borrar el registro?' });
  }, []);

  const confirmarCambiarEstado = useCallback((row) => {
    const mensaje = row.ACTIVO
      ? 'Esta seguro de desactivar este nivel de autorizacion?'
      : 'Esta seguro de activar este nivel de autorizacion?';
    setConfirmModal({ open: true, action: 'cambiarEstado', row, message: mensaje });
  }, []);

  const borrarNivelAut = useCallback(async (row) => {
    setLoadingAction(true);
    try {
      await axios.post(`${apiUrl}/nivelAutEliminar/`, { valorBool: 0, ID: row.ID, SESSION: loggedInUsername });
      setLoadingAction(false);
      await handleOpenSweetAlert('Registro eliminado con exito', 'success');
      fetchData(false);
    } catch (err) {
      console.error('Error', err);
      setLoadingAction(false);
      await handleOpenSweetAlert('Error al eliminar el registro', 'error');
    }
  }, [apiUrl, loggedInUsername, fetchData]);

  const handleConfirmAction = useCallback(() => {
    const { action, row } = confirmModal;
    if (action === 'borrar') borrarNivelAut(row);
    else if (action === 'cambiarEstado') cambiarEstadoNivelAut(row);
    setConfirmModal({ open: false, action: '', row: null, message: '' });
  }, [confirmModal, borrarNivelAut, cambiarEstadoNivelAut]);

  const handleCancelAction = useCallback(() => {
    setConfirmModal({ open: false, action: '', row: null, message: '' });
  }, []);

  // ─── Importar Excel
  const prettifyErrorMessage = (rawError = '') => {
    const msg = String(rawError);
    const parts = msg.split(':');
    if (parts.length > 1 && parts[0].trim().startsWith('ER_')) return parts.slice(1).join(':').trim();
    const erIndex = msg.indexOf('ER_');
    if (erIndex !== -1) {
      const sub = msg.slice(erIndex);
      const subParts = sub.split(':');
      if (subParts.length > 1) return subParts.slice(1).join(':').trim();
    }
    return msg;
  };

  const handleImportExcelNivelAut = useCallback(async () => {
    if (!excelData || excelData.length === 0) {
      setSnackbarMessage('No hay datos para importar. Verifica el archivo.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    setLoadingAction(true);
    try {
      const resp = await axios.post(`${apiUrl}/nivelAutImportarExcel`, {
        SESSION: loggedInUsername,
        rows: excelData,
      });

      const procesados = resp.data?.procesados || [];
      const errores = resp.data?.errores || [];
      const okCount = procesados.length;
      const errorCount = errores.length;

      setLoadingAction(false);

      if (errorCount > 0) {
        const detallesHtml = errores.map((e) => `<li><strong>Error en fila ${e.fila ?? '?'}:</strong> ${prettifyErrorMessage(e.error)}</li>`).join('');
        const resumenHtml = `
          <div style="text-align:left; font-size:14px;">
            <p><strong>Importacion finalizada.</strong></p>
            <p>Registros correctos: <strong>${okCount}</strong><br/>Registros con error: <strong>${errorCount}</strong></p>
            <hr/>
            <p><strong>Detalle de errores:</strong></p>
            <div style="max-height:260px; overflow-y:auto;"><ul style="padding-left:20px; margin:0;">${detallesHtml}</ul></div>
          </div>`;
        await handleOpenSweetAlert(resumenHtml, 'warning', { useHtml: true, width: '48rem' });
        setSnackbarMessage(`Importacion con advertencias: ${okCount} filas OK, ${errorCount} con error.`);
        setSnackbarSeverity('warning');
      } else {
        const htmlOk = `<div style="text-align:left; font-size:14px;"><p><strong>Importacion realizada con exito.</strong></p><p>Filas procesadas: <strong>${okCount}</strong>.</p></div>`;
        await handleOpenSweetAlert(htmlOk, 'success', { useHtml: true, width: '32rem' });
        setSnackbarMessage('Importacion realizada con exito.');
        setSnackbarSeverity('success');
      }

      setSnackbarOpen(true);
      await fetchData(false);
      setExcelData([]);
    } catch (err) {
      console.error('Error importando Excel de niveles:', err);
      setLoadingAction(false);
      setSnackbarMessage('Error al importar el archivo. Revisa el formato o intenta de nuevo.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [apiUrl, loggedInUsername, excelData, fetchData]);

  // ─── Sincronización Automática con ERP ──────────────────────────────────────
  const [loadingSync, setLoadingSync] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null); // { empresas, totalNuevos }
  const [modalSincronizarOpen, setModalSincronizarOpen] = useState(false);

  // Paso 1: obtener preview (sin guardar)
  const handlePreviewSincronizar = useCallback(async () => {
    if (!apiUrl) return;
    setLoadingPreview(true);
    setPreviewData(null);
    try {
      const res = await axios.post(`${apiUrl}/nivelAutPreviewSincronizar`, { idEmpresa });
      setPreviewData(res.data);
    } catch (err) {
      const msg = err.response?.data?.error || 'Error al obtener preview del ERP';
      handleOpenSweetAlert(msg, 'error');
      setModalSincronizarOpen(false);
    } finally {
      setLoadingPreview(false);
    }
  }, [apiUrl, idEmpresa]);

  // Paso 2: guardar con config por CC
  // configArray: [{ empresaId, ccId, autorizadores: [id,...], tesorero: id|null }]
  const handleSincronizarErp = useCallback(async (configArray) => {
    if (!apiUrl || !configArray?.length) return;
    setLoadingSync(true);
    try {
      const res = await axios.post(`${apiUrl}/nivelAutSincronizarErp`, {
        idEmpresa,
        usuarioId,
        configPorEmpresa: configArray,
      });
      if (res.data?.success) {
        handleOpenSweetAlert(res.data.mensaje || 'Sincronización finalizada', 'success');
        setModalSincronizarOpen(false);
        setPreviewData(null);
        fetchData();
      } else {
        handleOpenSweetAlert(res.data?.error || 'Error al sincronizar con ERP', 'error');
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Error de conexión al sincronizar con ERP';
      handleOpenSweetAlert(msg, 'error');
    } finally {
      setLoadingSync(false);
    }
  }, [apiUrl, idEmpresa, usuarioId, fetchData]);

  return {
    // Datos
    data,
    loadingData,
    loadingAction,
    loadingSync,
    loadingPreview,
    previewData,
    usuarioId,
    idEmpresa,
    empresaIds,
    empresaIdPref,
    loggedInUsername,
    nombreEmpresa,
    filteredData, // <-- Exponemos el estado a la vista

    // Datos auxiliares para modales
    dataEmpresa,
    dataDivisas,
    dataSucursal,
    dataCentrocosto,
    dataDepartamento,
    dataUsuario,
    dataUsuarioAutorizadores,
    dataUsuarioTesoreros,
    dataRolesUsuario,

    // Estado modales
    modalOpen,
    setModalOpen,
    modalCentroCostoOpen,
    setModalCentroCostoOpen,
    modalUsuarioOpen,
    setModalUsuarioOpen,
    modalDepartamentoOpen,
    setModalDepartamentoOpen,
    confirmModal,
    snackbarOpen,
    setSnackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    modalSincronizarOpen,
    setModalSincronizarOpen,

    // Formularios
    formData,
    setFormData,
    formCentroCosto,
    setFormCentroCosto,
    formDepartamento,
    setFormDepartamento,
    nuevoUsuario,
    setNuevoUsuario,
    campoOrigenUsuario,
    excelData,
    setExcelData,
    filterData,
    setFilterData,

    // Acciones
    fetchData,
    handleExportFetchAll,
    handleTableStateChange,
    handleOpenModal,
    handleCloseModal,
    handleInputChange,
    crearEditarRegistro,
    cambiarEstadoNivelAut,
    confirmarBorrar,
    confirmarCambiarEstado,
    handleConfirmAction,
    handleCancelAction,
    handleGuardarNuevoUsuario,
    handleImportExcelNivelAut,
    handleSincronizarErp,
    handlePreviewSincronizar,
    resetModalStates,

    // Setters de dependencias
    setDataCentrocosto,
    setDataDepartamento,
  };
};
